// =============================================================================
// Policy Editor Internal Representation (IR)
// =============================================================================
// Structured model for visual Rego policy editing. The admin never sees Rego
// syntax -- this IR is what the UI reads/writes, and rego-generator.ts
// converts it to valid OPA Rego on save.
// =============================================================================

// -----------------------------------------------------------------------------
// Scalar & Composite Values
// -----------------------------------------------------------------------------

export type RegoScalar = string | number | boolean | null;

export interface RegoObjectLiteral {
    kind: 'object';
    entries: { key: string; value: RegoValue }[];
}

export interface RegoArrayLiteral {
    kind: 'array';
    items: RegoValue[];
}

export interface RegoSetLiteral {
    kind: 'set';
    items: RegoValue[];
}

export interface RegoReference {
    kind: 'reference';
    path: string;
}

export interface RegoScalarValue {
    kind: 'scalar';
    value: RegoScalar;
}

export interface RegoFunctionCall {
    kind: 'functionCall';
    name: string;
    args: RegoValue[];
}

export interface RegoComprehension {
    kind: 'comprehension';
    type: 'array' | 'set' | 'object';
    term: string;
    keyTerm?: string;
    body: string;
}

export interface RegoRawExpression {
    kind: 'raw';
    text: string;
}

export type RegoValue =
    | RegoScalarValue
    | RegoObjectLiteral
    | RegoArrayLiteral
    | RegoSetLiteral
    | RegoReference
    | RegoFunctionCall
    | RegoComprehension
    | RegoRawExpression;

// -----------------------------------------------------------------------------
// Condition / Expression Types (used in rule bodies)
// -----------------------------------------------------------------------------

export type ComparisonOperator = '==' | '!=' | '<' | '>' | '<=' | '>=';
export type ArithmeticOperator = '+' | '-' | '*' | '/' | '%';

export interface ComparisonCondition {
    type: 'comparison';
    left: string;
    operator: ComparisonOperator;
    right: string;
}

export interface MembershipCondition {
    type: 'membership';
    term: string;
    collection: string;
    negated?: boolean;
}

export interface AssignmentCondition {
    type: 'assignment';
    variable: string;
    expression: string;
}

export interface IterationCondition {
    type: 'iteration';
    variables: string[];
    collection: string;
}

export interface UniversalCondition {
    type: 'universal';
    variables: string[];
    collection: string;
    body: Condition[];
}

export interface NegationCondition {
    type: 'negation';
    expression: string;
}

export interface FunctionCallCondition {
    type: 'functionCall';
    name: string;
    args: string[];
    assignTo?: string;
}

export interface RawCondition {
    type: 'raw';
    text: string;
}

export type Condition =
    | ComparisonCondition
    | MembershipCondition
    | AssignmentCondition
    | IterationCondition
    | UniversalCondition
    | NegationCondition
    | FunctionCallCondition
    | RawCondition;

// -----------------------------------------------------------------------------
// Policy Element Types
// -----------------------------------------------------------------------------

export interface PolicyImport {
    path: string;
    alias?: string;
}

/** Else clause for ordered rules */
export interface ElseClause {
    id: string;
    value: RegoValue;
    conditions: Condition[];
}

/** Constant / data block (e.g., llm_models = { ... }) */
export interface ConstantElement {
    id: string;
    elementType: 'constant';
    name: string;
    description?: string;
    value: RegoValue;
    isDefault?: boolean;
}

/**
 * Single-value (complete) rule.
 * `allow if { ... }` or `default allow := false`
 */
export interface SingleValueRuleElement {
    id: string;
    elementType: 'singleValueRule';
    name: string;
    description?: string;
    value: RegoValue;
    conditions: Condition[];
    isDefault?: boolean;
    elseChain?: ElseClause[];
}

/** Multi-value set rule: `set_name contains member if { ... }` */
export interface SetRuleElement {
    id: string;
    elementType: 'setRule';
    name: string;
    description?: string;
    memberExpression: string;
    conditions: Condition[];
}

/** Multi-value object rule: `obj[key] := value if { ... }` */
export interface ObjectRuleElement {
    id: string;
    elementType: 'objectRule';
    name: string;
    description?: string;
    keyExpression: string;
    valueExpression: string;
    conditions: Condition[];
}

/** Function definition (one branch -- OR is multiple elements with same name) */
export interface FunctionElement {
    id: string;
    elementType: 'function';
    name: string;
    description?: string;
    parameters: string[];
    returnValue: string;
    conditions: Condition[];
}

/**
 * Raw/opaque Rego block for constructs the parser couldn't decompose.
 * Displayed as a read-only card; admin can delete but not visually edit.
 */
export interface RawRegoElement {
    id: string;
    elementType: 'rawRego';
    name: string;
    description?: string;
    source: string;
}

export type PolicyElement =
    | ConstantElement
    | SingleValueRuleElement
    | SetRuleElement
    | ObjectRuleElement
    | FunctionElement
    | RawRegoElement;

// -----------------------------------------------------------------------------
// Root Document
// -----------------------------------------------------------------------------

export interface PolicyDocument {
    packagePath: string;
    imports: PolicyImport[];
    elements: PolicyElement[];
}

// -----------------------------------------------------------------------------
// UI Display Helpers
// -----------------------------------------------------------------------------

export type ElementCategory =
    | 'decision'
    | 'data'
    | 'rule'
    | 'function'
    | 'raw';

const DECISION_NAMES = new Set(['decision', 'allow', 'deny']);

export function getElementCategory(el: PolicyElement): ElementCategory {
    if (el.elementType === 'rawRego') return 'raw';
    if (el.elementType === 'constant') return 'data';
    if (el.elementType === 'function') return 'function';
    if (
        (el.elementType === 'singleValueRule' ||
            el.elementType === 'objectRule') &&
        DECISION_NAMES.has(el.name)
    ) {
        return 'decision';
    }
    return 'rule';
}

export function elementTypeLabel(type: PolicyElement['elementType']): string {
    switch (type) {
        case 'constant': return 'Data Block';
        case 'singleValueRule': return 'Rule';
        case 'setRule': return 'Set Rule';
        case 'objectRule': return 'Object Rule';
        case 'function': return 'Function';
        case 'rawRego': return 'Raw Rego';
    }
}

export const EMPTY_POLICY_DOCUMENT: PolicyDocument = {
    packagePath: '',
    imports: [],
    elements: [],
};
